import { Box, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import DialogueActions from "src/views/assistant/components/header/DialogueActions";
import { useVnvAssistStore } from "../store/VnvAssistStore";
import { ChatMessageWithParts } from "src/views/assistant/types";

type VnvAssistHeaderProps = {
  onNewChat: () => void;
  dialogueId: number | null;
  dialogue: ChatMessageWithParts[];
};

const VnvAssistHeader = ({
  onNewChat,
  dialogueId,
  dialogue,
}: VnvAssistHeaderProps) => {
  const {
    validationStatus,
    claims,
    identificationTotalChunks,
    identificationChunksCompleted,
  } = useVnvAssistStore();

  const isIdentifying = validationStatus === "identifying";
  const isIdentified = validationStatus === "identified";
  const totalCount = claims.length;

  // Two sub-states during identification:
  //   (a) task not yet confirmed (no total_chunks event yet) → "Preparing document…"
  //   (b) task running → "Identifying… X claims · Y / N chunks"
  // On a mid-run reload chunksDone briefly reads as 0 until the next
  // chunk_progress event lands; acceptable trade-off for not persisting
  // chunk progress in the DB.
  let identifyingLabel = "Preparing document…";
  if (identificationTotalChunks !== null) {
    const claimsPart = `${totalCount} claim${totalCount !== 1 ? "s" : ""}`;
    const chunksDone = identificationChunksCompleted ?? 0;
    const chunksPart = `${chunksDone} / ${identificationTotalChunks} chunks`;
    identifyingLabel = `Identifying… ${claimsPart} · ${chunksPart}`;
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        px: 2,
        py: 1,
        borderBottom: 1,
        borderColor: "divider",
        gap: 1,
        flexShrink: 0,
      }}
    >
      <VerifiedIcon color="primary" fontSize="small" />
      <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
        VNV Assist
      </Typography>

      {isIdentifying && (
        <Chip
          icon={<CircularProgress size={12} />}
          label={identifyingLabel}
          size="small"
          variant="outlined"
        />
      )}
      {isIdentified && totalCount > 0 && (
        <Chip
          label={`${totalCount} claim${totalCount !== 1 ? "s" : ""} identified`}
          size="small"
          color="info"
          variant="outlined"
        />
      )}

      <Box sx={{ ml: 1 }}>
        <DialogueActions
          dialogueId={dialogueId}
          onNewChat={onNewChat}
          dialogue={dialogue}
        />
      </Box>
    </Stack>
  );
};

export default VnvAssistHeader;
